import Gooey, { calc, collection, field, mount, ref } from '@srhazi/gooey';
import type { Component } from '@srhazi/gooey';
import * as Parser from 'parsinator';

import './web-tools.css';

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    (r /= 255), (g /= 255), (b /= 255);

    var max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    var h,
        s,
        l = (max + min) / 2;

    if (max == min) {
        h = s = 0; // achromatic
    } else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                h = 0;
                break;
        }

        h /= 6;
    }

    return [h, s, l];
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    var r, g, b;

    if (s == 0) {
        r = g = b = l; // achromatic
    } else {
        function hue2rgb(p: number, q: number, t: number) {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        }

        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
}

/**
 * Converts an RGB color value to HSV. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSV_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and v in the set [0, 1].
 */
function rgbToHsv(r: number, g: number, b: number) {
    (r /= 255), (g /= 255), (b /= 255);

    var max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    var h: number,
        s,
        v = max;

    var d = max - min;
    s = max == 0 ? 0 : d / max;

    if (max == min) {
        h = 0; // achromatic
    } else {
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
                h = 0;
                break;
        }

        h /= 6;
    }

    return [h, s, v];
}

/**
 * Converts an HSV color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSV_color_space.
 * Assumes h, s, and v are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 */
function hsvToRgb(h: number, s: number, v: number) {
    var r, g, b;

    var i = Math.floor(h * 6);
    var f = h * 6 - i;
    var p = v * (1 - s);
    var q = v * (1 - f * s);
    var t = v * (1 - (1 - f) * s);

    switch (i % 6) {
        case 0:
            (r = v), (g = t), (b = p);
            break;
        case 1:
            (r = q), (g = v), (b = p);
            break;
        case 2:
            (r = p), (g = v), (b = t);
            break;
        case 3:
            (r = p), (g = q), (b = v);
            break;
        case 4:
            (r = t), (g = p), (b = v);
            break;
        case 5:
            (r = v), (g = p), (b = q);
            break;
        default:
            (r = 0), (g = 0), (b = 0);
            break;
    }

    return [r * 255, g * 255, b * 255];
}

const ws = Parser.regex(/\s+/);
const ows = Parser.regex(/\s*/);

const parseNumber = Parser.fromGenerator(function* () {
    return yield* Parser.map(
        Parser.regex(/[-+]?[0-9]+(\.[0-9]*)?(e-?[0-9]*)?/),
        (val) => parseFloat(val)
    );
});
const parsePercent = Parser.fromGenerator(function* () {
    const number = yield* parseNumber;
    const pct = yield* Parser.str('%');
    return number;
});

const parseAngle = Parser.fromGenerator(function* () {
    const angle = yield* parseNumber;
    const unit = yield* Parser.choice([
        Parser.str('deg'),
        Parser.str('turn'),
        Parser.str('rad'),
        Parser.str('grad'),
    ]);
    return {
        angle,
        unit,
    };
});

const parseHsl = Parser.fromGenerator(function* () {
    yield* Parser.str('hsl(');
    yield* ows;
    const hue = yield* Parser.choice([
        parseAngle,
        Parser.map(parseNumber, (angle) => ({ angle, unit: 'deg' as const })),
    ]);
    yield* ws;
    const saturation = yield* Parser.choice([parsePercent, parseNumber]);
    yield* ws;
    const luminance = yield* Parser.choice([parsePercent, parseNumber]);
    yield* ows;
    yield* Parser.str(')');
    let hueClamped: number;
    switch (hue.unit) {
        case 'deg':
            hueClamped = (hue.angle / 360) % 1;
            break;
        case 'grad':
            hueClamped = (hue.angle / 400) % 1;
            break;
        case 'rad':
            hueClamped = (hue.angle / (2 * Math.PI)) % 1;
            break;
        case 'turn':
            hueClamped = hue.angle % 1;
            break;
    }
    return hslToRgb(hueClamped, saturation / 100, luminance / 100);
});

const parseRgb = Parser.fromGenerator(function* () {
    yield* Parser.str('#');
    const [r, g, b] = yield* Parser.choice([
        Parser.sequence([
            Parser.regex(/[0-9A-Fa-f]{2}/),
            Parser.regex(/[0-9A-Fa-f]{2}/),
            Parser.regex(/[0-9A-Fa-f]{2}/),
            Parser.map(Parser.end, () => ''),
        ]),
        Parser.sequence([
            Parser.regex(/[0-9A-Fa-f]/),
            Parser.regex(/[0-9A-Fa-f]/),
            Parser.regex(/[0-9A-Fa-f]/),
            Parser.map(Parser.end, () => ''),
        ]),
    ]);
    if (r.length === 1) {
        return [
            parseInt(r + '0', 16),
            parseInt(g + '0', 16),
            parseInt(b + '0', 16),
        ];
    }
    return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
});

const clamp = (min: number, val: number, max: number) => {
    if (val < min) return min;
    if (val > max) return max;
    return val;
};

const RGBtoHSL = () => {
    const rgbEl = ref<HTMLInputElement>();
    const hslEl = ref<HTMLInputElement>();
    const colorEl = ref<HTMLInputElement>();
    const rgbField = field('#000000');
    const hslField = field('hsl(0deg 0% 0%)');
    const colorField = field('#000000');
    const focusedField = field<'rgb' | 'hsl' | 'color'>('rgb');
    const parsedColor = field({ r: 0, g: 0, b: 0 });

    const toPercent = (n: number) =>
        (Math.round(n * 10000) / 100).toString() + '%';
    const toHex = (n: number) =>
        clamp(0, Math.round(n), 255).toString(16).padStart(2, '0');

    function setColor(r: number, g: number, b: number) {
        const focused = focusedField.get();
        if (focused !== 'rgb') {
            rgbField.set(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
        }
        if (focused !== 'hsl') {
            const [h, s, l] = rgbToHsl(r, g, b);
            hslField.set(`hsl(${h * 360}deg ${toPercent(s)} ${toPercent(l)})`);
        }
        if (focused !== 'color') {
            colorField.set(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
        }
    }

    return (
        <fieldset class="RGBtoHSL">
            <legend>RGB ↔ HSL ↔ HSV</legend>
            <label for="RGBtoHSL_rgb">RGB:</label>
            <input
                id="RGBtoHSL_rgb"
                ref={rgbEl}
                type="text"
                value={rgbField}
                on:input={(e, el) => {
                    rgbField.set(el.value);
                    try {
                        const [r, g, b] = Parser.runToEnd(parseRgb, el.value);
                        setColor(r, g, b);
                        el.setCustomValidity('');
                    } catch (e) {
                        el.setCustomValidity('Not a valid RGB string');
                    }
                }}
                on:focus={() => {
                    focusedField.set('rgb');
                }}
                on:blur={(e, el) => {
                    el.checkValidity();
                }}
            />
            <button
                on:click={() =>
                    rgbEl.current &&
                    navigator.clipboard.writeText(rgbEl.current.value)
                }
            >
                Copy
            </button>
            <label for="RGBtoHSL_hsl">HSL:</label>
            <input
                id="RGBtoHSL_hsl"
                ref={hslEl}
                type="text"
                value={hslField}
                on:input={(e, el) => {
                    hslField.set(el.value);
                    try {
                        const [r, g, b] = Parser.runToEnd(parseHsl, el.value);
                        setColor(r, g, b);
                        el.setCustomValidity('');
                    } catch (e) {
                        console.log(e);
                        el.setCustomValidity('Not a valid HSL color string');
                    }
                    el.checkValidity();
                }}
                on:focus={() => {
                    focusedField.set('hsl');
                }}
                on:blur={(e, el) => {
                    el.checkValidity();
                }}
            />
            <button
                on:click={() =>
                    hslEl.current &&
                    navigator.clipboard.writeText(hslEl.current.value)
                }
            >
                Copy
            </button>
            <label for="RGBtoHSL_color">Color:</label>
            <input
                id="RGBtoHSL_color"
                ref={colorEl}
                type="color"
                value={colorField}
                on:input={(e, el) => {
                    colorField.set(el.value);
                    try {
                        const [r, g, b] = Parser.runToEnd(parseRgb, el.value);
                        setColor(r, g, b);
                        el.setCustomValidity('');
                    } catch (e) {
                        el.setCustomValidity('Not a valid color');
                    }
                }}
                on:focus={() => {
                    focusedField.set('color');
                }}
                on:blur={(e, el) => {
                    el.checkValidity();
                }}
            />
            <button
                on:click={() =>
                    colorEl.current &&
                    navigator.clipboard.writeText(colorEl.current.value)
                }
            >
                Copy
            </button>
        </fieldset>
    );
};

const Tools = () => (
    <div class="Tools">
        <RGBtoHSL />
    </div>
);

const rootEl = document.getElementById('app');
if (rootEl) {
    mount(rootEl, <Tools />);
}
